import { Injectable } from '@angular/core';
import { AsamcoAuthService, CommonServices } from '@asamco/base';
import { fixOdataDateOffsetFilter, pathJoin, showErrorSwal } from '@asamco/utils';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2';

import { Company } from '../company/company.interface';
import { Organisation, organisationDefaults } from './organisation.interface';

@Injectable({
	providedIn: 'root',
})
export class OrganisationService {
	currentData: Organisation[] = [];

	currentData$ = new ReplaySubject<Organisation[]>(1);

	constructor(private cs: CommonServices, private auth: AsamcoAuthService) {}

	/** Gets all organisations */
	public getAll() {
		return this.cs.http.get(`organisation/allowedorganisations`).pipe(
			concatMap((result: any[]) => {
				this.currentData = result.map(r => Object.assign(organisationDefaults(), r));
				this.currentData$.next(this.currentData);
				return this.currentData$;
			})
		);
	}

	public getAllCompanies() {
		return this.cs.http.get<Company[]>(`organisation/allowedcompanieswithorganisation`);
	}

	/**
	 * Gets a single Organisation
	 * @param organisationId id of the organisation to get
	 */
	public get(organisationId: number): Observable<Organisation> {
		return this.cs.http.get<Organisation>(`organisation/${organisationId}`);
	}

	/**
	 * Saves an Organisation
	 * @param organisation the saved organisation
	 */
	public save(organisation: Organisation) {
		return this.cs.http.post(`organisation`, organisation).pipe(
			tap((returnedOrganisation: Organisation) => {
				organisation.id = returnedOrganisation.id;
				this.currentData.splice(
					this.currentData.findIndex(d => d.id === organisation.id),
					1,
					organisation
				);
				this.currentData$.next(this.currentData);
			})
		);
	}

	/**
	 * Deletes an Organisation by its ID
	 * @param organisationId the id of the organisation to delete
	 */
	public delete(organisationId: number): Observable<Organisation> {
		return from(
			swal.fire({
				icon: 'warning',
				title: this.cs.ts.translate('SWAL_TITLE.WARNING'),
				html: this.cs.ts.translate('ORGANISATION.DIALOG.DELETE_QUESTION'),
				showCancelButton: true,
			})
		).pipe(
			switchMap(result => {
				return result.value ? this.cs.http.delete<Organisation>(`organisation/${organisationId}`) : from([]);
			}),
			tap(organisation => {
				this.currentData.splice(
					this.currentData.findIndex(d => d.id === organisation.id),
					1
				);
				this.currentData$.next(this.currentData);
				notify(this.cs.ts.translate('ORGANISATION.DIALOG.DELETED'), 'success');
			}),
			catchError(error => {
				showErrorSwal(error);
				return of(error);
			})
		);
	}

	/**
	 * Gets all authorized users for an organisation
	 * @param companyId
	 */
	public getAuthorizedUsers(organisationId: number) {
		return this.cs.http.get(`userorganisationaccess/fororganisation/${organisationId}`);
	}

	/**
	 * Grant access to the given users
	 * @param users
	 * @param organisationId
	 */
	public grantAccess(userIds: number[], organisationId: number) {
		return this.cs.http.post('userorganisationaccess/grantaccess', { users: userIds, itemId: organisationId });
	}

	/**
	 * Revoke access to the given users
	 * @param user
	 * @param organisationId
	 */
	public revokeAccess(userId: number, organisationId: number) {
		return this.cs.http.post('userorganisationaccess/revokeaccess', { userId: userId, itemId: organisationId });
	}

	public getOrganisationUsersOdataUrl(organisationId: number, withAccess: boolean) {
		return pathJoin([
			this.auth.userSession.backend.odataBaseUrl,
			`OrganisationUsers/Organisation(OrganisationId=${organisationId},UsersWithAccess=${withAccess})`,
		]);
	}

	public getOrganisationUsersOdataDataSource(organisationId: number, usersWithAccess: boolean): DataSource {
		return new DataSource({
			paginate: true,
			requireTotalCount: true,
			store: new ODataStore({
				version: 4,
				url: this.getOrganisationUsersOdataUrl(organisationId, usersWithAccess),
				beforeSend: (request: { params: Object; headers: Object }) => {
					this.auth.dxStoreBeforeSend(request);
				},
				deserializeDates: false,
				onLoading: options => {
					fixOdataDateOffsetFilter(options.filter);
				},
			}),
		});
	}
}
